import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { getPushPermissionState, requestNotificationPermission, getPushPlatform } from '../services/pushService';
import { openSystemSettings } from '../components/NotificationReengagementBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLOCKED_MSG = "Attiva l'interruttore Notifiche per ricevere gol e risultato finale";

// Shared across components: followed fixture ids for the current user.
let cache = null;
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn(cache || []));

// Returns true only when the phone permission is granted (asks the OS if still possible).
const ensurePushPermission = async () => {
  const state = await getPushPermissionState().catch(() => 'unsupported');
  if (state === 'granted') return true;
  if (state === 'denied') {
    toast(BLOCKED_MSG, { duration: 7000 });
    if (Capacitor.isNativePlatform()) await openSystemSettings();
    return false;
  }
  if (state === 'unsupported') {
    toast.error('Le notifiche non sono disponibili su questo dispositivo');
    return false;
  }
  let token = null;
  try { token = await requestNotificationPermission(); } catch { token = null; }
  if (token) await axios.post(`${API}/notifications/register-token`, { token, platform: getPushPlatform() }).catch(() => {});
  return (await getPushPermissionState().catch(() => 'unsupported')) === 'granted';
};

export const useMatchFollows = (enabled) => {
  const [ids, setIds] = useState(cache || []);
  useEffect(() => {
    if (!enabled) return undefined;
    listeners.add(setIds);
    if (!cache) {
      axios.get(`${API}/match-follows`).then((r) => { cache = r.data?.fixture_ids || []; emit(); }).catch(() => {});
    }
    return () => { listeners.delete(setIds); };
  }, [enabled]);

  const isFollowing = (fixtureId) => ids.includes(Number(fixtureId));

  const toggle = async (match) => {
    if (!enabled) { toast('Accedi per attivare le notifiche della partita'); return; }
    const fid = Number(match.fixture_id);
    if (isFollowing(fid)) {
      await axios.delete(`${API}/match-follows/${fid}`).catch(() => {});
      cache = (cache || []).filter((i) => i !== fid);
      emit();
      toast('Notifiche disattivate per questa partita');
      return;
    }
    if (!(await ensurePushPermission())) return;
    try {
      await axios.post(`${API}/match-follows/${fid}`, {
        home_team: match.home_team, away_team: match.away_team,
        match_date: match.match_date || '', match_time: match.match_time || '',
      });
      cache = [...(cache || []), fid];
      emit();
      toast.success('Notifiche attive per questa partita');
    } catch {
      toast.error('Impossibile attivare le notifiche, riprova');
    }
  };

  return { isFollowing, toggle };
};
